import { computed, DOCUMENT, effect, inject, Service } from '@angular/core';
import { UserSettingEventConsumerService } from '../events/user-setting/user-setting-event-consumer.service';

/**
 * The class that puts the app's بدنه clear of the home indicator. Applied to the
 * document element rather than to a component so the footer, any portaled bottom
 * sheet and the page behind them all move together — see `styles.scss`.
 */
const BOTTOM_SPACING_CLASS = 'has-bottom-spacing';

/**
 * The class that renders every digit the app paints in Persian. On the document
 * element because it works by a font feature that inherits — see `styles.scss`.
 */
const PERSIAN_DIGITS_CLASS = 'persian-digits';

/**
 * تنظیمات عمومی as the rest of the app reads them.
 *
 * Every flag is a `computed` over the event bus rather than a signal of its own,
 * for the same reason `ThemeService` reads the theme that way: the cached records
 * are already in hand at startup, so the first paint of a session is in the
 * trader's settings rather than the defaults with a correction a moment later.
 *
 * {@link bottomSpacing} and {@link persianDigits} are acted on here, because both
 * are about the document itself. The rest belong to whoever can honour them:
 * {@link keepScreenAwake} to `ScreenWakeService`, {@link hideBalances} to the
 * screens that print a balance.
 */
@Service()
export class GeneralSettingsService {
	private readonly doc = inject(DOCUMENT);
	private readonly userSetting = inject(UserSettingEventConsumerService);

	private readonly general = computed(() => this.userSetting.getSetting().general);

	/** «فاصله اپلیکیشن از پایین صفحه» */
	readonly bottomSpacing = computed(() => this.general().notchDown);
	/** «روشن نگه داشتن صفحه‌نمایش» — acted on by `ScreenWakeService`. */
	readonly keepScreenAwake = computed(() => this.general().noSleep);
	/** «عدم نمایش مانده» — true while balances are to be shown masked. */
	readonly hideBalances = computed(() => this.general().noBalance);
	/** «نمایش اعداد فارسی» — true while digits are to be rendered ۰-۹. */
	readonly persianDigits = computed(() => this.general().usePersianNumber);

	constructor() {
		effect(() => {
			this.doc.documentElement.classList.toggle(BOTTOM_SPACING_CLASS, this.bottomSpacing());
		});

		effect(() => {
			this.doc.documentElement.classList.toggle(PERSIAN_DIGITS_CLASS, this.persianDigits());
		});
	}
}
